import React, { useEffect, useState } from "react";
import { OfflineAssetsContext } from "./useOfflineAssets";
import { useLocalStorage } from "../../utilities/useLocalStorage";
import { OfflineBuilderAsset } from "../../entities/OfflineBuilderAsset";
import { BuilderAsset } from "../../entities/BuilderAsset";
import { BuilderAssetService } from "../../services/BuilderAssetService";
import { ServerResponse } from "../../services/server/OldWebClient";
import { useOfflineStatus } from "../../utilities/useOfflineStatus";
import { OfflineManagerNotifications } from "./OfflineManagerNotifications";
import { useUser } from "../../utilities/useUser";
import { AssetDataType } from "../../entities/AssetType";
import { toAssetCategory } from "../../entities/AssetCategory";
import { ConflictedAsset, BuilderAssetConflictDialog, ConflictStatus } from "./BuilderAssetConflictDialog";
import { EventTracking, OfflineActionLocation } from "../../utilities/Tracking/EventTracking";
import { useLocation } from "react-router-dom";

const resolveUpdatedOfflineAssets = async (offlineBuilderAssets: OfflineBuilderAsset[], serverBuilderAssets: BuilderAsset[]) => {
	const editedOfflineAssets = offlineBuilderAssets.filter((a) => a.editedOffline);
	// Asset was either deleted or no long marked for offline use
	const editedOfflineAssetsWithoutOnlineCounterParts = editedOfflineAssets.filter(
		(offlineAsset) => serverBuilderAssets.find((serverAsset) => offlineAsset.id === serverAsset.id) === undefined
	);

	const conflictsToResolve: ConflictedAsset[] = await checkServerForOfflineBuilderAsset(editedOfflineAssetsWithoutOnlineCounterParts);

	// This should remove any offline assets that are deleted or don't belong to the user
	const offlineAssetsThatAreMarkedAsOnlineOnlyNow = editedOfflineAssetsWithoutOnlineCounterParts.filter((a) =>
		conflictsToResolve.some((c) => c.offline.id === a.id)
	);

	const updatedOfflineAssets: OfflineBuilderAsset[] = serverBuilderAssets
		.map((serverAsset) => {
			const conflict = editedOfflineAssets.find((editedAsset) => editedAsset.id === serverAsset.id);
			if (conflict) {
				conflictsToResolve.push({ online: serverAsset, offline: conflict });
				return conflict;
			}
			const offlineAsset: OfflineBuilderAsset = { ...serverAsset, editedOffline: false };
			return offlineAsset;
		})
		.concat(offlineAssetsThatAreMarkedAsOnlineOnlyNow);

	return { updatedOfflineAssets, conflictsToResolve };
};

const checkServerForOfflineBuilderAsset = (editedOfflineAssetsWithoutOnlineCounterParts: OfflineBuilderAsset[]) => {
	const conflictsToResolve: ConflictedAsset[] = [];

	editedOfflineAssetsWithoutOnlineCounterParts.forEach(async (offlineAsset) => {
		const response = await BuilderAssetService.get(offlineAsset.id);
		// User changed the offline preference for this asset
		if (ServerResponse.isSuccess(response)) {
			conflictsToResolve.push({ online: response.data, offline: offlineAsset });
		}
		// If user either deleted this asset or it's garbage we're going to ignore it
	});

	return conflictsToResolve;
};

const deserializeOfflineBuilderAsset = (asset: OfflineBuilderAsset): OfflineBuilderAsset => {
	return {
		...asset,
		category: toAssetCategory(asset.category),
		dateAccessed: new Date(asset.dateAccessed),
		dateCreated: new Date(asset.dateCreated),
		dateModified: new Date(asset.dateModified),
		datePreached: asset.datePreached ? new Date(asset.datePreached) : undefined,
		assetType: AssetDataType.EditableText,
	};
};

interface Props {}

export function OfflineManager(props: React.PropsWithChildren<Props>) {
	const isOffline = useOfflineStatus();
	const userInfo = useUser();
	const location = useLocation();
	const { onlineUser } = userInfo;

	const [offlineMode, setOfflineMode] = useState(isOffline);
	const [serailizedOfflineBuilderAssets, setOfflineBuilderAssets] = useLocalStorage<OfflineBuilderAsset[]>("offlineBuilderAssets", []);
	const [offlineBuilderAssets, setDeseriealizedOfflineBuilderAssets] = useState<OfflineBuilderAsset[]>([]);

	useEffect(() => {
		setDeseriealizedOfflineBuilderAssets(serailizedOfflineBuilderAssets.map<OfflineBuilderAsset>(deserializeOfflineBuilderAsset));
	}, [serailizedOfflineBuilderAssets]);

	const [conflictedAssets, setConflictedAssets] = useState<ConflictedAsset[]>([]);
	const [conflictStatus, setConflictStatus] = useState<ConflictStatus>({ total: 0, current: 0 });

	const [offlineModeChangeMessage, setOfflineModeChangeMessage] = useState<string | undefined>();
	
	useEffect(() => {
		// Don't attempt fetch unless someone is online and logged in
		if (!offlineMode && onlineUser) {			
			fetchUpdatedAssetsMarkedAsOffline();
		}

		async function fetchUpdatedAssetsMarkedAsOffline() {
			const response = await BuilderAssetService.getOfflineBuilderAssets();
			if (ServerResponse.isSuccess(response)) {
				await reconcileOnlineAssetsWithOffline(response.data);
			}
		}

		async function reconcileOnlineAssetsWithOffline(serverAssets: BuilderAsset[]) {
			const { updatedOfflineAssets, conflictsToResolve } = await resolveUpdatedOfflineAssets(offlineBuilderAssets, serverAssets);
			setOfflineBuilderAssets(updatedOfflineAssets);
			setConflictedAssets(conflictsToResolve);
			setConflictStatus({ total: conflictsToResolve.length, current: 1 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onlineUser, offlineMode]);

	const setAssetOfflineMode = async (builderAssetId: string, offlineMode: boolean, actionLocation?: OfflineActionLocation) => {
		const response = await BuilderAssetService.setOfflineMode(builderAssetId, offlineMode);
		if (ServerResponse.isSuccess(response)) {
			if (offlineMode) {
				setOfflineModeChangeMessage("Available offline");
				addAssetToOfflineStorage(response.data);
				if(actionLocation)
					EventTracking.Offline.enableOffline(actionLocation, offlineBuilderAssets.length + 1);
			} else {
				setOfflineModeChangeMessage("No longer available offline");
				removeAssetFromOfflineStorage(builderAssetId);
				if(actionLocation)
					EventTracking.Offline.disableOffline(actionLocation, offlineBuilderAssets.length + 1);
			}
			return response.data;
		}

		return null;
	};

	const addAssetToOfflineStorage = (builderAsset: BuilderAsset) => {
		const offlineAsset: OfflineBuilderAsset = {
			...builderAsset,
			editedOffline: false,
		};
		setOfflineBuilderAssets([...offlineBuilderAssets, offlineAsset]);
	};

	const removeAssetFromOfflineStorage = (builderAssetId: string) => {
		setOfflineBuilderAssets([...offlineBuilderAssets].filter((a) => a.id !== builderAssetId));
	};

	const updateAssetInOfflineStorage = (builderAsset: BuilderAsset) => {
		const offlineAsset: OfflineBuilderAsset = {
			...builderAsset,
			dateModified: new Date(),
			dateAccessed: new Date(),
			editedOffline: offlineMode,
		};
		const updatedAssets: OfflineBuilderAsset[] = offlineBuilderAssets.map((a) => {
			if (a.id === offlineAsset.id) {
				return offlineAsset;
			}
			return a;
		});
		setOfflineBuilderAssets(updatedAssets);
		return offlineAsset;
	};

	const onConflictResolved = (id: string, builderAsset: BuilderAsset | null) => {
		const conflicts = conflictedAssets.filter((a) => a.offline.id !== id);
		setConflictedAssets(conflicts);

		if (conflicts.length > 0) {
			setConflictStatus({ ...conflictStatus, current: conflictStatus.current + 1 });
		} else {
			EventTracking.Offline.conflictResolved(conflictStatus.total);
		}

		if (!builderAsset) {
			removeAssetFromOfflineStorage(id);
			return;
		}

		const offlineAsset: OfflineBuilderAsset = {
			...builderAsset,
			editedOffline: false,
		};
		const updatedAssets: OfflineBuilderAsset[] = offlineBuilderAssets.map((a) => {
			if (a.id === offlineAsset.id) {
				return offlineAsset;
			}
			return a;
		});
		setOfflineBuilderAssets(updatedAssets);

		if (location.pathname.includes("/build/")) {
			window.location.reload();
		}
	};

	return (
		<OfflineAssetsContext.Provider
			value={{
				onlineMode: !offlineMode,
				offlineBuilderAssets: offlineBuilderAssets,
				toggleOfflineMode: (offlineMode) => setOfflineMode(offlineMode),
				setAssetOfflineMode,
				saveBuilderAssetOffline: updateAssetInOfflineStorage,
			}}
		>
			{props.children}
			<OfflineManagerNotifications
				offlineMode={offlineMode}
				onChangeOfflineMode={(offlineMode) => setOfflineMode(offlineMode)}
				offlineModeChangeMessage={offlineModeChangeMessage}
				onDismissOfflineModeChangeMessage={() => setOfflineModeChangeMessage(undefined)}
			/>
			<BuilderAssetConflictDialog conflictedAssets={conflictedAssets} status={conflictStatus} onConflictResolved={onConflictResolved} />
		</OfflineAssetsContext.Provider>
	);
}
